import { SEARCH_KEY_MAPPING, SearchKeyConfig } from '@/lib/config/search/searchKeyMapping';
import { buildBreadcrumbItemsFromPath } from '@/lib/functions/buildBreadcrumbItemsFromPath';
import { collectNavCategoryPaths } from '@/lib/functions/collectNavCategoryPaths';
import type { BreadcrumbItem } from '@/lib/types/breadcrumbs';
import type { SyliusAttribute } from '@/lib/types/products';

export interface ProductBreadcrumbHints {
  attributes?: SyliusAttribute[];
  description?: string;
  taxonomy?: string;
}

const NAV_CATEGORY_PATHS = collectNavCategoryPaths();

// Parent category for products that do not match a sidebar category.
const SHOP_FALLBACK_CATEGORY_PATH = 'all-products';

// Slug/title terms that identify a category when Sylius only exposes a generic /shop/gifts/ URL.
const CATEGORY_URL_HINTS: Record<string, string[]> = {
  'soda-syrups': ['bib', 'syrup'],
};

function normalizePath(path: string): string {
  return path.replace(/^\/+|\/+$/g, '');
}

function getProductSlugFromUrl(productUrl: string): string {
  const segments = normalizePath(productUrl).split('/').filter(Boolean);
  const shopIndex = segments.indexOf('shop');
  const pathSegments = shopIndex >= 0 ? segments.slice(shopIndex + 1) : segments;
  return pathSegments.at(-1) ?? '';
}

const BROAD_SUBCATEGORIES = new Set(['nightlife party supplies']);

function normalizeTaxonomySegment(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, ' ');
}

function parseTildeTaxonomy(raw: string): { category: string | null; subcategory: string | null } {
  const parts = raw.split('~').map((part) => part.trim()).filter(Boolean);

  return {
    category: parts[0] ? normalizeTaxonomySegment(parts[0]) : null,
    subcategory: parts[1] ? normalizeTaxonomySegment(parts[1]) : null,
  };
}

interface ProductTaxonomyLevels {
  category: string | null;
  subcategory: string | null;
}

function parseProductTaxonomyLevels(
  attributes?: SyliusAttribute[],
  taxonomy?: string,
): ProductTaxonomyLevels {
  let category: string | null = null;
  let subcategory: string | null = null;

  for (const attribute of attributes ?? []) {
    const code = attribute.code?.toLowerCase() ?? '';
    const value = attribute.value == null ? '' : String(attribute.value).trim();
    if (!value) {
      continue;
    }

    if (code === 'category' && !category) {
      category = normalizeTaxonomySegment(value);
      continue;
    }

    if (code === 'subcategory' && !subcategory) {
      subcategory = normalizeTaxonomySegment(value);
      continue;
    }

    if (value.includes('~')) {
      const parsed = parseTildeTaxonomy(value);
      category = category ?? parsed.category;
      subcategory = subcategory ?? parsed.subcategory;
    }
  }

  if (taxonomy) {
    const parsed = parseTildeTaxonomy(taxonomy);
    category = category ?? parsed.category;
    subcategory = subcategory ?? parsed.subcategory;
  }

  return { category, subcategory };
}

function buildAttributeMap(attributes?: SyliusAttribute[], taxonomy?: string): Map<string, string> {
  const map = new Map<string, string>();

  for (const attribute of attributes ?? []) {
    const code = attribute.code?.toLowerCase();
    if (!code || attribute.value == null || attribute.value === '') {
      continue;
    }

    map.set(code, String(attribute.value).toLowerCase());
  }

  const levels = parseProductTaxonomyLevels(attributes, taxonomy);
  if (levels.category && !map.has('category')) {
    map.set('category', levels.category);
  }
  if (levels.subcategory && !map.has('subcategory')) {
    map.set('subcategory', levels.subcategory);
  }

  return map;
}

function buildProductHaystack(
  productTitle: string,
  productSlug: string,
  description?: string,
  attributes?: SyliusAttribute[],
): string {
  const attributeText = (attributes ?? [])
    .map((attribute) => attribute.value)
    .filter(Boolean)
    .join(' ');

  return [productTitle, productSlug, description, attributeText]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

function getQueryTerms(config: SearchKeyConfig): string[] {
  if (config.type !== 'query') {
    return [];
  }

  return (Array.isArray(config.query) ? config.query : [config.query])
    .map((term) => term.trim().toLowerCase())
    .filter(Boolean);
}

function facetKeyToAttributeCode(facetKey: string): string {
  return facetKey.replace(/\.keyword$/i, '').replace(/_/g, '').toLowerCase();
}

function isBroadFacetValue(facetKey: string, value: string): boolean {
  const normalized = value.toLowerCase();

  if (facetKey === 'category.keyword' && normalized === 'gifts') {
    return true;
  }

  // Parent Party Supplies search key; most gift SKUs carry this subcategory.
  if (facetKey === 'subcategory.keyword' && BROAD_SUBCATEGORIES.has(normalized)) {
    return true;
  }

  return false;
}

function hasSpecificFacetMatch(config: SearchKeyConfig, attributeMap: Map<string, string>): boolean {
  const facets = config.facets;
  if (!facets) {
    return false;
  }

  return Object.entries(facets).some(([facetKey, values]) => {
    if (values.every((value) => isBroadFacetValue(facetKey, value))) {
      return false;
    }

    const attributeValue = attributeMap.get(facetKeyToAttributeCode(facetKey));
    if (!attributeValue) {
      return false;
    }

    return values.some((value) => attributeValue.includes(value.toLowerCase()));
  });
}

function scoreCategoryPath(
  path: string,
  config: SearchKeyConfig,
  haystack: string,
  attributeMap: Map<string, string>,
): number {
  const depth = path.split('/').filter(Boolean).length;
  let score = 0;

  for (const term of getQueryTerms(config)) {
    if (haystack.includes(term)) {
      score = Math.max(score, 100 * depth + term.length);
    }
  }

  if (hasSpecificFacetMatch(config, attributeMap)) {
    score = Math.max(score, 50 * depth + path.length);
  }

  const urlHints = CATEGORY_URL_HINTS[path];
  if (urlHints?.some((term) => haystack.includes(term))) {
    score = Math.max(score, 75 * depth + path.length);
  }

  return score;
}

function resolveCategoryPath(
  haystack: string,
  attributeMap: Map<string, string>,
): string | null {
  let bestPath: string | null = null;
  let bestScore = 0;

  for (const path of NAV_CATEGORY_PATHS) {
    if (path === SHOP_FALLBACK_CATEGORY_PATH) {
      continue;
    }

    const config = SEARCH_KEY_MAPPING[path];
    if (!config) {
      continue;
    }

    const score = scoreCategoryPath(path, config, haystack, attributeMap);
    if (score > bestScore || (score === bestScore && path.length > (bestPath?.length ?? 0))) {
      bestPath = path;
      bestScore = score;
    }
  }

  return bestScore > 0 ? bestPath : null;
}

function getTopLevelCategoryPath(categoryPath: string): string {
  return categoryPath.split('/').filter(Boolean)[0] ?? categoryPath;
}

// GIFTS~NIGHTLIFE PARTY SUPPLIES only maps to a nav leaf, not a top-level sidebar group.
function shouldUseShopFallbackForTaxonomy(
  categoryPath: string | null,
  taxonomy: ProductTaxonomyLevels,
): boolean {
  if (!categoryPath || !taxonomy.category || !taxonomy.subcategory) {
    return false;
  }

  if (taxonomy.category === 'gifts' && BROAD_SUBCATEGORIES.has(taxonomy.subcategory)) {
    return !categoryPath.includes('/');
  }

  return false;
}

export function resolveProductCategoryPath(
  productUrl: string,
  productTitle: string,
  hints: ProductBreadcrumbHints & { slug?: string } = {},
): string | null {
  const productSlug = hints.slug ?? getProductSlugFromUrl(productUrl);
  const taxonomy = parseProductTaxonomyLevels(hints.attributes, hints.taxonomy);
  const attributeMap = buildAttributeMap(hints.attributes, hints.taxonomy);
  const haystack = buildProductHaystack(
    productTitle,
    productSlug,
    hints.description,
    hints.attributes,
  );

  const categoryPath = resolveCategoryPath(haystack, attributeMap);

  if (shouldUseShopFallbackForTaxonomy(categoryPath, taxonomy)) {
    return null;
  }

  return categoryPath;
}

/**
 * Storefront product URL under the top-level sidebar category (e.g. /bottle-service/bib-pepsi-pbg/).
 */
export function resolveProductUrl(
  productUrl: string,
  productTitle: string,
  hints: ProductBreadcrumbHints & { slug?: string } = {},
): string {
  const productSlug = hints.slug ?? getProductSlugFromUrl(productUrl);
  const categoryPath = resolveProductCategoryPath(productUrl, productTitle, hints);

  if (categoryPath && productSlug) {
    return `/${getTopLevelCategoryPath(categoryPath)}/${productSlug}/`;
  }

  if (productSlug) {
    return `/shop/${productSlug}/`;
  }

  return '/shop/';
}

function isShopFallbackProductUrl(url: string): boolean {
  const segments = normalizePath(url).split('/').filter(Boolean);
  return segments[0] === 'shop' && segments.length === 2;
}

/**
 * Builds PDP breadcrumbs aligned with sidebar categories via SEARCH_KEY_MAPPING.
 */
export function buildProductBreadcrumbs(
  productUrl: string,
  productTitle: string,
  hints: ProductBreadcrumbHints & { slug?: string } = {},
): BreadcrumbItem[] {
  const resolvedUrl = resolveProductUrl(productUrl, productTitle, hints);
  const categoryPath = resolveProductCategoryPath(productUrl, productTitle, hints);

  if (categoryPath && !isShopFallbackProductUrl(resolvedUrl)) {
    return [
      ...buildBreadcrumbItemsFromPath(getTopLevelCategoryPath(categoryPath)),
      { label: productTitle, href: resolvedUrl },
    ];
  }

  return [
    ...buildBreadcrumbItemsFromPath(SHOP_FALLBACK_CATEGORY_PATH),
    { label: productTitle, href: resolvedUrl },
  ];
}
